<template>
  <form-wrapper :validator="$root.$v.client">
    <v-container class="px-5">
      <v-row>
        <v-col cols="12">
          <h1 class="mt-0">
            {{ $t('client.header') }}
          </h1>
          <p class="mb-0">
            {{ $t('client.desc') }}
          </p>
        </v-col>
        <v-col
          cols="12"
          lg="9"
        >
          <v-row>
            <v-col
              v-if="!client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.firstName"
                :validator="$root.$v.client.firstName"
                label="firstName"
                :maxlength="41"
                @update:model="$root.updateClient('firstName', $event)"
              />
            </v-col>
            <v-col
              v-if="!client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.lastName"
                :validator="$root.$v.client.lastName"
                label="lastName"
                :maxlength="41"
                @update:model="$root.updateClient('lastName', $event)"
              />
            </v-col>
            <v-col
              v-if="client.isCompany"
              cols="12"
              md="6"
            >
              <label class="input-label">{{ $t('labels.companyType') }}</label>
              <form-group
                name="companyType"
                :validator="$root.$v.client.companyType"
              >
                <template slot-scope="{ attrs }">
                  <v-select
                    v-model="client.companyType"
                    :items="companyTypes"
                    item-text="text"
                    item-value="value"
                    v-bind="attrs"
                    @change="$root.updateClient('companyType', client.companyType)"
                    @input="$root.$v.client.companyType.$touch()"
                  />
                </template>
              </form-group>
            </v-col>
            <v-col
              v-if="client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.businessId"
                :validator="$root.$v.client.businessId"
                label="businessId"
                :maxlength="9"
                @update:model="$root.updateClient('businessId', $event); getBusinessIdDataFromYTJ()"
              />
            </v-col>
            <v-col
              v-if="client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.companyName"
                :validator="$root.$v.client.companyName"
                label="companyName"
                :maxlength="41"
                @update:model="$root.updateClient('companyName', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.address"
                :validator="$root.$v.client.address"
                label="address"
                :maxlength="40"
                @update:model="$root.updateClient('address', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.postalNumber"
                :validator="$root.$v.client.postalNumber"
                label="postalNumber"
                :maxlength="5"
                @update:model="$root.updateClient('postalNumber', $event)"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.city"
                :validator="$root.$v.client.city"
                label="city"
                :maxlength="50"
                @update:model="$root.updateClient('city', $event)"
              />
            </v-col>
            <v-col
              v-if="!client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.phoneNumber"
                :validator="$root.$v.client.phoneNumber"
                label="phoneNumber"
                :maxlength="14"
                @update:model="$root.updateClient('phoneNumber', $event)"
              />
            </v-col>
            <v-col
              v-if="!client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.email"
                :validator="$root.$v.client.email"
                label="email"
                :maxlength="51"
                @update:model="$root.updateClient('email', $event)"
              />
            </v-col>
            <v-col
              v-if="!client.isCompany"
              cols="12"
              sm="6"
            >
              <custom-input-group
                :model="client.socSecNr"
                :validator="$root.$v.client.socSecNr"
                label="socSecNr"
                :maxlength="11"
                @update:model="$root.updateClient('socSecNr', $event)"
              />
            </v-col>
            <v-col cols="12"
                   sm="6">
              <label class="input-label">
                <span class="mr-3">{{ $t('labels.country') }}</span>
                <v-tooltip bottom max-width="300px">
                  <template v-slot:activator="{ on }">
                    <v-icon color="primary" dark v-on="on">fas fa-info-circle</v-icon>
                  </template>
                  <span>{{ $t('client.foreignCompanyTooltip') }}</span>
                </v-tooltip>
              </label>
              <form-group name="country">
                <template slot-scope="{ attrs }">
                  <v-text-field :value="$t('common.finland')"
                                disabled
                                v-bind="attrs" />
                </template>
              </form-group>
            </v-col>
            <v-col cols="12"
                   :class="{ 'col-sm-7': $root.BICIsRequired }">
              <label class="input-label">
                <span class="mr-3">{{ $t('labels.IBAN') }} *</span>
                <v-tooltip bottom max-width="300px">
                  <template v-slot:activator="{ on }">
                    <v-icon color="primary" dark v-on="on">fas fa-info-circle</v-icon>
                  </template>
                  <span>{{ $t('client.IBANTooltip') }}</span>
                </v-tooltip>
              </label>
              <custom-input-group :model="client.IBAN"
                                  :validator="$root.$v.client.IBAN"
                                  :maxlength="32"
                                  :showLabel="false"
                                  :label="IBAN"
                                  @update:model="$root.updateClient('IBAN', $event)" />
            </v-col>
            <v-col
              v-if="$root.BICIsRequired"
              cols="12"
              sm="5"
            >
              <custom-input-group
                :model="client.BIC"
                :validator="$root.$v.client.BIC"
                label="BIC"
                :maxlength="11"
                @update:model="$root.updateClient('BIC', $event)"
              />
            </v-col>
            <template v-if="client.isCompany">
              <v-col cols="12">
                <h5>
                  {{ $t('client.contactPerson') }}
                  <v-tooltip bottom max-width="300px">
                    <template v-slot:activator="{ on }">
                      <v-icon color="primary" dark v-on="on">fas fa-info-circle</v-icon>
                    </template>
                    <span>{{ $t('client.contactPersonTooltip') }}</span>
                  </v-tooltip>
                </h5>
              </v-col>
              <v-col cols="12"
                     sm="6">
                <custom-input-group :model="client.contactPerson.firstName"
                                    :validator="$root.$v.client.contactPerson.firstName"
                                    label="firstName"
                                    :maxlength="41"
                                    @update:model="$root.updateContactPerson('firstName', $event)" />
              </v-col>
              <v-col cols="12"
                     sm="6">
                <custom-input-group :model="client.contactPerson.lastName"
                                    :validator="$root.$v.client.contactPerson.lastName"
                                    label="lastName"
                                    :maxlength="41"
                                    @update:model="$root.updateContactPerson('lastName', $event)" />
              </v-col>
              <v-col cols="12"
                     sm="6">
                <custom-input-group :model="client.contactPerson.phoneNumber"
                                    :validator="$root.$v.client.contactPerson.phoneNumber"
                                    label="phoneNumber"
                                    :maxlength="14"
                                    @update:model="$root.updateContactPerson('phoneNumber', $event)" />
              </v-col>
              <v-col cols="12"
                     sm="6">
                <custom-input-group :model="client.contactPerson.email"
                                    :validator="$root.$v.client.contactPerson.email"
                                    label="email"
                                    :maxlength="51"
                                    @update:model="$root.updateContactPerson('email', $event)" />
              </v-col>

              <v-col cols="12">
                <h5>
                  {{ $t('client.vatObligation') }}
                </h5>
              </v-col>
              <v-col cols="12"
                     sm="6">
                <label class="input-label">{{ $t('labels.vatObligation') }}</label>
                <form-group name="vatObligation"
                            :validator="$root.$v.client.vatObligation">
                  <template slot-scope="{ attrs }">
                    <v-select v-model="client.vatObligation"
                              :validator="$root.$v.client.vatObligation"
                              :items="VatYesNo"
                              item-text="text"
                              item-value="value"
                              v-bind="attrs"
                              :label="$t('labels.select')"
                              @change="$root.updateClient('vatObligation', client.vatObligation)"
                              @input="$root.$v.client.vatObligation.$touch()" />
                  </template>
                </form-group>
              </v-col>
            </template>
            <v-col
              class="text-center"
              cols="12"
            >
              <v-btn
                large
                tile
                :disabled="!stepperStatus.client.complete"
                :class="{ primary: stepperStatus.client.complete }"
                to="money-laundering"
              >
                {{ $t('common.continue') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-dialog
        v-model="importFromYTJDialog"
        :fullscreen="$root.dialogSettings.fullscreen"
        :scrollable="$root.dialogSettings.scrollable"
        :hide-overlay="$root.dialogSettings.hideoverlay"
        :transition="$root.dialogSettings.transition"
        :width="$root.dialogSettings.width"
        :max-width="$root.dialogSettings.maxwidth"
        persistent
      >
        <v-card>
          <v-card-title primary-title
                        class="text-h5 primary white--text">
            {{ $t('ytj.header') }}
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <p>{{ $t('ytj.text-1') }} <b>{{ prhData.businessId }}</b> {{ $t('ytj.text-2') }} <b>{{ prhData.companyName }}</b></p>
                  <p>{{ $t('ytj.question') }}</p>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary"
                   text
                   @click="importFromYTJDialog = false">
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              text
              @click="savePrhData"
            >
              {{ $t('common.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </form-wrapper>
</template>

<script>
  import axios from 'axios'

  export default {
    data: () => ({
      importFromYTJDialog: false,
      prhData: {
        businessId: '',
        companyName: '',
        address: {
          street: '',
          postCode: '',
          city: '',
          country: ''
        }
      }
    }),
    computed: {
      client () {
        return this.$store.getters.client
      },
      companyTypes () {
        let companyTypes = []

        for (let i = 1; i <= 10; i++) {
          companyTypes.push({ value: i, text: this.$t('companyType.' + i) })
        }

        return companyTypes
      },
      VatYesNo () {
        return [
          {
            value: 1, text: this.$t('labels.yes')
          },
          {
            value: 2, text: this.$t('labels.no')
          }
        ]
      },
      stepperStatus () {
        return this.$store.getters.stepperStatus
      }
    },
    methods: {
      savePrhData () {
        if (this.prhData.address.companyName !== '') { this.$root.updateClient('companyName', this.prhData.companyName) }
        if (this.prhData.address.street !== '') { this.$root.updateClient('address', this.prhData.address.street) }
        if (this.prhData.address.postCode !== '') { this.$root.updateClient('postalNumber', this.prhData.address.postCode) }
        if (this.prhData.address.city !== '') { this.$root.updateClient('city', this.prhData.address.city) }
        // if (this.prhData.address.country !== '') { this.$root.updateClient('country', this.prhData.address.country) }

        this.$root.$v.client.$touch()
        this.importFromYTJDialog = false
      },
      getBusinessIdDataFromYTJ () {
        var value = this.client.businessId
        if (value.length === 8) { value = value.substr(0, 7) + '-' + value.substr(7, 8) }

        if (value.length === 9 && value.indexOf('-') > 0) {
          axios.get('https://avoindata.prh.fi/bis/v1/' + value).then((response) => {
            if (response.data && response.data.results != null && response.data.results.length > 0) {
              var result = response.data.results[0]

              this.prhData.businessId = value
              this.prhData.companyName = result.name
              this.prhData.address.street = ''
              this.prhData.address.postCode = ''
              this.prhData.address.city = ''
              // this.prhData.address.country = ''

              if (result.addresses.length > 0) {
                var address = result.addresses[result.addresses.length - 1]

                this.prhData.address.street = address.street
                this.prhData.address.postCode = address.postCode
                this.prhData.address.city = address.city
                // this.prhData.address.country = address.country
              }

              this.importFromYTJDialog = true
            }
          })
        }
      }
    },
    beforeRouteLeave (to, from, next) {
      if (this.$root.$v.client.$anyDirty) {
        this.$root.$v.client.$touch()
      }
      next()
    }
  }
</script>
